import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { fetchAccounts } from '../reducers/snowflakeAccountsSlice';
import { useAppDispatch } from 'app/hooks';
import { XMarkIcon } from '@heroicons/react/20/solid';
import LoadingView from 'views/LoadingView';
import { generateCodeChallenge, generateRandomString } from '../../../react-oauth2-code-pkce/pkceUtils';

interface SnowflakeAccountLoginInnerViewProps {
  authType: 'auth0' | 'platform';
  usePKCE?: boolean;
  pkceAuthorizationEndpoint?: string;
  pkceClientId?: string;
  pkceRedirectUri?: string;
  pkceScope?: string;
}

export default function SnowflakeAccountLoginInnerView(props: SnowflakeAccountLoginInnerViewProps): JSX.Element {
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const integrationId = searchParams.get('integrationId');
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const warehouse = searchParams.get('warehouse');
  const role = searchParams.get('role');
  const accountId = searchParams.get('account_id');
  const redirectUrl = searchParams.get('redirect_url');

  const { usePKCE, pkceAuthorizationEndpoint, pkceClientId, pkceRedirectUri, pkceScope } = props;

  const hasStarted = useRef<boolean>(false);
  const [finished, setFinished] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | false>(false);
  const [showError, setShowError] = useState<boolean>(false);

  useEffect(() => {
    if (integrationId != null && code != null && state != null) {
      if (hasStarted.current) return;
      hasStarted.current = true;
      let url = `/api/v1/snowflake/account`;
      let method = 'post';
      if (accountId !== null) {
        url = `/api/v1/snowflake/account/${accountId}`;
        method = 'put';
      }
      const redirectParamPairs: string[][] = [
        ['integrationId', integrationId ?? ''],
        ['role', role ?? ''],
        ['warehouse', warehouse ?? ''],
        ['auth_type', props.authType],
      ];
      if (accountId !== null) {
        redirectParamPairs.push(['account_id', accountId]);
      }
      if (redirectUrl !== null) {
        redirectParamPairs.push(['redirect_url', redirectUrl]);
      }
      const redirectParams = new URLSearchParams(redirectParamPairs);

      const codeVerifier = usePKCE === true ? sessionStorage.getItem('SNOWFLAKE_PKCE_code_verifier') : null;
      const storedState = usePKCE === true ? sessionStorage.getItem('SNOWFLAKE_PKCE_state') : null;
      if (usePKCE === true) {
        if (storedState !== state) {
          sessionStorage.removeItem('SNOWFLAKE_PKCE_code_verifier');
          sessionStorage.removeItem('SNOWFLAKE_PKCE_state');
          setErrorMessage('State mismatch — possible CSRF attempt.');
          setShowError(true);
          setFinished(true);
          return;
        }
        sessionStorage.removeItem('SNOWFLAKE_PKCE_code_verifier');
        sessionStorage.removeItem('SNOWFLAKE_PKCE_state');
      }

      axios
        .request({
          url,
          method,
          data: {
            type: 'oauth',
            code,
            ...(usePKCE === true && codeVerifier !== null ? { code_verifier: codeVerifier } : {}),
            warehouse,
            role,
            oauth_integration_id: integrationId,
            redirect_url: `${window.location.origin}/snowflake-accounts/auth?${redirectParams.toString()}`,
          },
        })
        .then(() => {
          setSuccess(true);
        })
        .catch((err) => {
          console.log(err);
          setErrorMessage(err.response.data);
          setShowError(true);
        })
        .finally(() => {
          setFinished(true);
          dispatch(fetchAccounts()).catch((err) => err);
        });
    } else {
      if (!finished && !hasStarted.current) {
        if (
          usePKCE === true &&
          pkceAuthorizationEndpoint !== undefined &&
          pkceAuthorizationEndpoint !== '' &&
          pkceClientId !== undefined &&
          pkceClientId !== '' &&
          pkceRedirectUri !== undefined &&
          pkceRedirectUri !== '' &&
          pkceScope !== undefined &&
          pkceScope !== ''
        ) {
          hasStarted.current = true;
          const codeVerifier = generateRandomString(96);
          sessionStorage.setItem('SNOWFLAKE_PKCE_code_verifier', codeVerifier);
          const pkceState = generateRandomString(32);
          sessionStorage.setItem('SNOWFLAKE_PKCE_state', pkceState);
          generateCodeChallenge(codeVerifier)
            .then((codeChallenge) => {
              const authParams = new URLSearchParams({
                response_type: 'code',
                client_id: pkceClientId,
                redirect_uri: pkceRedirectUri,
                scope: pkceScope,
                state: pkceState,
                code_challenge: codeChallenge,
                code_challenge_method: 'S256',
              });
              window.location.replace(`${pkceAuthorizationEndpoint}?${authParams.toString()}`);
            })
            .catch((err) => console.log(err));
        } else {
          auth.signinRedirect().catch((err) => err);
        }
      }
    }
  }, [auth]);

  useEffect(() => {
    if (!finished || showError) return;
    const dest = (() => {
      if (redirectUrl?.startsWith('/') === true && !redirectUrl.startsWith('//')) return redirectUrl;
      if (props.authType === 'platform') {
        return process.env.REACT_APP_ENV === 'az'
          ? 'https://create.az.dataops.live/dataops-oauth-callback'
          : process.env.REACT_APP_ENV === 'production'
          ? 'https://create.dataops.live/dataops-oauth-callback'
          : 'https://create.qa.dataops.live/dataops-oauth-callback';
      }
      return '/snowflake-accounts';
    })();
    const timer = setTimeout(() => {
      window.location.href = dest;
    }, 1500);
    return () => clearTimeout(timer);
  }, [finished, showError]);

  if (finished && success && !showError) {
    return (
      <div className="h-full min-w-0 flex-1 flex items-center justify-center">
        <div className="rounded-md bg-green-50 border-2 border-green-500 p-6 text-center">
          <p className="text-sm text-green-700">Snowflake account connected successfully. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showError && (
        <div className="h-full min-w-0 flex-1">
          <>
            <div className="fixed top-[7.2rem] left-16 right-0 z-10 rounded-md bg-red-50 p-4">
              <div className="flex w-full">
                <div className="ml-3 w-full">
                  <div className="flex">
                    <h3 className="flex-grow text-sm font-medium text-red-800">Update error</h3>
                    <button
                      type="button"
                      onClick={() => setShowError(false)}
                      className="h-5 w-5 mr-4 inline-flex items-center rounded-full text-red-800 hover:bg-red-100"
                    >
                      <XMarkIcon className="w-full" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="w-full text-sm text-red-800">{errorMessage}</div>
                </div>
              </div>
            </div>
            <div className="mt-[7.5rem]"></div>
          </>
        </div>
      )}
      {!showError && <LoadingView />}
    </>
  );
}
